import { Component, OnInit, inject, signal, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, switchMap, filter, take } from 'rxjs';
import { SubscriptionService } from '../../../../core/services/subscription.service';
import { SettingsService } from '../../../../core/services/settings.service';
import { Store } from '@ngrx/store';
import { selectCompanyId } from '../../../../core/store/auth/auth.selectors';
import { toast } from 'ngx-sonner';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { financialStatusKey } from '../../../../core/utils/financial-status.utils';
import { BranchPlanCard } from './branch-plan-card';

@Component({
  selector: 'app-subscriptions-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, BranchPlanCard],
  templateUrl: './subscriptions-tab.html',
})
export class SubscriptionsTab implements OnInit {
  readonly getFinancialStatusKey = financialStatusKey;
  private subscriptionService = inject(SubscriptionService);
  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private perm = inject(PermissionService);

  get canCreate(): boolean { return this.perm.canCreate(MODULE.CONFIGURACOES); }
  get canDelete(): boolean { return this.perm.canDelete(MODULE.CONFIGURACOES); }

  @Output() navigateToTab = new EventEmitter<string>();

  subscriptionData$ = new BehaviorSubject<any>(null);
  paymentHistory$ = new BehaviorSubject<any[]>([]);
  isLoadingSubscription = signal(false);
  companyId: number | null = null;
  currentModalState: 'none' | 'missing_info' | 'plan_details' | 'cancel_confirmation' = 'none';
  selectedPlan: any = null;

  ngOnInit() {
    this.loadSubscriptionData();
  }

  private loadSubscriptionData() {
    this.isLoadingSubscription.set(true);
    
    this.store.select(selectCompanyId).pipe(
      filter(id => !!id),
      take(1),
      switchMap(companyId => {
        this.companyId = companyId;
        console.log('SubscriptionsTab: Fetching subscription data for company:', companyId);
        return this.subscriptionService.getSubscriptionData(companyId);
      })
    ).subscribe({
      next: (data: any) => {
        console.log('SubscriptionsTab: Data received successfully', data);
        this.subscriptionData$.next(data);
        this.isLoadingSubscription.set(false);
        
        if (data?.subscription_plan_data?.company_id) {
          this.loadPaymentHistory(data.subscription_plan_data.company_id);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('SubscriptionsTab: Error loading subscription data', err);
        this.isLoadingSubscription.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  private loadPaymentHistory(companyId: number) {
    this.subscriptionService.getPaymentHistory(companyId).subscribe({
      next: (data: any) => {
        this.paymentHistory$.next(data || []);
        this.cdr.detectChanges();
      },
      error: (err) => console.error('SubscriptionsTab: Error loading payment history', err)
    });
  }

  showSubscribeModal() {
    if (!this.canCreate) return;
    if (!this.companyId) return;

    // Fetch company data to validate
    this.settingsService.getCompanyData(this.companyId).pipe(take(1)).subscribe({
      next: (companyData) => {
        if (this.checkMissingInfo(companyData)) {
          this.currentModalState = 'missing_info';
          this.cdr.detectChanges();
        } else {
          // Valid info, fetch plan details
          this.subscriptionService.getSubscriptionPlans().pipe(take(1)).subscribe({
            next: (plans) => {
              this.selectedPlan = plans && plans.length > 0 ? plans[0] : null;
              this.currentModalState = 'plan_details';
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('Error loading plans', err);
              toast.error(this.translate.instant('common_error_generic'));
            }
          });
        }
      },
      error: (err) => {
        console.error('Error fetching company data for validation', err);
        toast.error(this.translate.instant('common_error_generic'));
      }
    });
  }

  closeModal() {
    this.currentModalState = 'none';
    this.cdr.detectChanges();
  }

  closeMissingInfoModalAndNavigate() {
    this.currentModalState = 'none';
    this.navigateToTab.emit('clinic');
    this.cdr.detectChanges();
  }

  showCancelConfirmation() {
    if (!this.canDelete) return;
    this.currentModalState = 'cancel_confirmation';
    this.cdr.detectChanges();
  }

  showPaymentModal(payment: any) {
    // For now, same as plan modal as in Flutter it points to same widget
    this.showSubscribeModal();
  }

  confirmCancelSubscription() {
    if (!this.canDelete) return;
    // Logic to cancel would go here (Supabase update)
    // For now, just a toast as in Flutter's basic implementation
    toast.success(this.translate.instant('subscription_cancel_success'));
    this.closeModal();
    this.loadSubscriptionData();
  }

  // Check if critical company validation info is missing
  checkMissingInfo(companyData: any): boolean {
    if (!companyData) return true;
    
    // Based on Flutter logic which checks:
    // establishmentAddress, postalCode, city, country, corporateReason (Firma)
    const { establishment_address, postal_code, city, country, corporate_reason } = companyData;
    
    return !establishment_address || !postal_code || !city || !country || !corporate_reason;
  }
}
